/** @module csi/comm/PostRequest */
define([
	"dojo/_base/declare",
	"dojo/has",
	"underscore",
	"csi/constants",
	"csi/util",
	"./Request"
], function(declare, has, _, constants, util, Request) {

	const MAX_NODES_PER_REQUEST = 100000;
	const PATH_WEIGHT = 3;

	const {
		DEP_PATH,
		FMT_COMPLETE,
		FMT_COMPLETE_MAX,
		MODULE_SEARCH,
		OPERATION_QUERY_URL,
		OPERATION_QUERY_XML,
		QRY_LIMIT_BEGIN,
		QRY_LIMIT_END,
		QRY_URL,
	} = constants;

	return declare(Request, {
		binaryKey: null,
		binaryQuery: null,
		binaryValue: null,
		binaryXml: null,

		/** @property {Object<string,boolean>} _fileKeys - map of keys whose value are files */
		_fileKeys: null,

		/**
		 * Add file(s) to request
		 * @param {File|File[]} [files] - either a single file or list of files to add to request
		 * @param {string} [key] - key to use for file parameter(s)
		 * @param {string} [keyParameter] - parameter that specifies key used for files
		 */
		addFiles: function(files, key, keyParameter) {
			if (_.isArray(files)) {
				key = key || "uploadedfiles[]";
			} else {
				key = key || "uploadedfile";
				files = [files];
			}

			// track parameters that hold files
			this._fileKeys = this._fileKeys || {};
			this._fileKeys[key] = true;

			// add parameter for files
			this.addParameter(key, files);

			// add parameter to indicate key holding the file data - this is necessary in C8 requests to publish files
			if (keyParameter) {
				this.addParameter(keyParameter, key);
			}

			return this;
		},

		setBinary: function(/*String*/key, /*String*/value, /*String?*/contentType, /*Boolean*/bXml) {
			this.binaryKey = key;
			this.binaryValue = value;

			if (bXml) {
				this.binaryXml = '<?xml version="1.0" encoding="utf-8" ?>\r\n' + value;
			}

			// reset query if binary set directly through setBinary
			this.binaryQuery = null;

			return this;
		},

		setBinaryQuery: function(/*String*/key, /*Query*/q) {
			// summary: set binary for request and keep track of query used to generate it
			var queryString = (q.bDirect || this.statics.disableQueryAccelerator) ? q.getDirectString() : q.toString();
			this.setBinary(key, queryString, null, true);
			this.binaryQuery = q;
		},

		go: function(ioArgs) {
			// summary: common interface for requests
			return this.post(ioArgs);
		},

		post: function(ioArgs) {
			// summary: compile arguments for request
			this._optimize();

			ioArgs = ioArgs || {};
			ioArgs.method = "POST";
			ioArgs.preventCache = false;
			ioArgs.url = ioArgs.url || this.urlBase;
			ioArgs.data = ioArgs.data || this._createFormData(this.content);

			//Manage handled responses based on request's mimeType
			if (this.content.MimeType) {
				ioArgs.handleAs = this.content.MimeType;
			}

			return this._xhr(ioArgs);
		},

		/**
		 * Return FormData object with parameters in request
		 * @param {Object} content
		 * @return {FormData}
		 */
		_createFormData: function(content) {
			var formData = new FormData();
			var fileKeys = this._fileKeys || {};

			function add(key, value) {
				// for FormData.append() the default filename for Blob objects is "blob", so the filename must be specified by passing its name
				if (fileKeys[key]) {
					formData.append(key, value, value.name);
				} else {
					formData.append(key, value);
				}
			}

			for (var x in content) {
				var y = content[x];
				if (y != null) {
					if (y instanceof Array) {
						for (var i = 0; i < y.length; ++i) {
							add(x, y[i]);
						}
					} else {
						add(x, y);
					}
				}
			}

			if (this.binaryKey && this.binaryValue) {
				// use binaryXml if available to get UTF-8 encoding
				formData.append(this.binaryKey, this.binaryXml || this.binaryValue);
			}

			return formData;
		},

		_splitRequest: function(/*Object*/ioArgs) {
			// summary: if search request is too large, then break up into multiple requests
			var content = this.content;
			var module = content.Module;
			if (module == MODULE_SEARCH) {
				var maxNodes = has("csi-nodes-per-request-threshold") || MAX_NODES_PER_REQUEST;
				var operation = content.Operation;
				var nPaths = _.size(content[DEP_PATH]) || 1;
				var nNodes = 0;

				if (operation == OPERATION_QUERY_URL) {
					nNodes = _.size([].concat(content[QRY_URL]));
				} else if (operation == OPERATION_QUERY_XML) {
					nNodes = content[QRY_LIMIT_END] - content[QRY_LIMIT_BEGIN] + 1;
				}

				// multiple number of nodes by a factor of the the number of paths to get a more realistic number;
				// right now though the PATH_WEIGHT is just a guess as to the number of nodes per path - it could vary widely
				nNodes *= nPaths * PATH_WEIGHT;

				if (nNodes > maxNodes) {
					var nRequests = Math.ceil(nNodes / maxNodes);
					var splitArgs;

					// split appropriate parameter(s) into an array of parameters, each for a separate request
					if (operation == OPERATION_QUERY_URL) {
						splitArgs = util.splitRequestArray(content, QRY_URL, nRequests);
					} else {
						splitArgs = util.splitRequestPages(content, QRY_LIMIT_BEGIN, QRY_LIMIT_END, nRequests);

						// just because a number of results are requested, that does not mean they exist;
						// so, provide an indication that not all requests may be necessary
						ioArgs.bTestSplitResults = true;
					}

					// create separate ioArgs for each request
					content = _.clone(content);

					return _.map(splitArgs, function(obj, i) {
						ioArgs = _.clone(ioArgs);
						ioArgs.data = this._createFormData(_.extend(content, obj));
						delete content[FMT_COMPLETE];
						delete content[FMT_COMPLETE_MAX];
						return ioArgs;
					}, this);
				}
			}

			return null;
		}
	});
});